// Chat History Service - API calls for ChatGPT-style chat history
import { trackHistoryAPI } from '../utils/apiTracker';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

class ChatHistoryService {
  constructor() {
    this.baseURL = `${getApiBaseUrl()}/api/chat-history`;
    
    // Rate limiting and request management
    this.requestQueue = [];
    this.isProcessingQueue = false;
    this.maxConcurrentRequests = 3;
    this.activeRequests = 0;
    this.rateLimitDelay = 1000; // 1 second between requests
    this.lastRequestTime = 0;
    this.abortController = new AbortController();
  }

  // Cancel all pending requests
  cancelAllRequests() {
    console.log('🚫 Cancelling all pending requests');
    this.abortController.abort();
    this.abortController = new AbortController();
    this.requestQueue = [];
    this.activeRequests = 0;
  }

  // Rate limiting helper
  async waitForRateLimit() {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    if (timeSinceLastRequest < this.rateLimitDelay) {
      const waitTime = this.rateLimitDelay - timeSinceLastRequest;
      console.log(`⏳ Rate limiting: waiting ${waitTime}ms`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    
    this.lastRequestTime = Date.now();
  }

  // Wait for available request slot
  async waitForRequestSlot() {
    while (this.activeRequests >= this.maxConcurrentRequests) {
      console.log(`⏳ Waiting for request slot. Active: ${this.activeRequests}/${this.maxConcurrentRequests}`);
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  // Enhanced fetch with rate limiting and retry logic
  async rateLimitedFetch(url, options = {}, retries = 3) {
    await this.waitForRateLimit();
    await this.waitForRequestSlot();
    
    this.activeRequests++;
    
    try {
      const fetchOptions = {
        ...options,
        signal: this.abortController.signal
      };
      
      const response = await trackHistoryAPI(url, fetchOptions);
      
      // Handle rate limiting
      if (response.status === 429) {
        console.warn('⚠️ Rate limited, retrying with exponential backoff');
        if (retries > 0) {
          const backoffDelay = Math.pow(2, 3 - retries) * 1000; // 1s, 2s, 4s
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
          return this.rateLimitedFetch(url, options, retries - 1);
        }
        throw new Error('Too many requests. Please try again later.');
      }
      
      return response;
    } finally {
      this.activeRequests--;
    }
  }

  // Get auth headers with JWT token
  getAuthHeaders() {
    // Try both token names for compatibility
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    console.log('Getting auth headers, token exists:', !!token);
    console.log('Token source:', localStorage.getItem('authToken') ? 'authToken' : localStorage.getItem('token') ? 'token' : 'none');
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Only add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  // Get all conversations for the current user
  async getConversations(limit = 50) {
    try {
      console.log('🔄 ChatHistoryService.getConversations called with limit:', limit);
      console.log('🔄 Base URL:', this.baseURL);
      
      const headers = this.getAuthHeaders();
      console.log('🔄 Auth headers:', headers);
      
      const url = `${this.baseURL}/conversations?limit=${limit}`;
      console.log('🔄 Making request to:', url);
      
      // Use regular fetch instead of rateLimitedFetch for debugging
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'include' // Include cookies for authentication
      });

      console.log('🔄 Response status:', response.status);
      console.log('🔄 Response ok:', response.ok);
      console.log('🔄 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorText = await response.clone().text();
          console.error('🔄 Response not ok, error text:', errorText);
          errorMessage += `, message: ${errorText}`;
        } catch (textError) {
          console.error('🔄 Could not read error text:', textError);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('🔄 Response data:', data);
      console.log('🔄 Conversations array:', data.conversations);
      console.log('🔄 Number of conversations:', data.conversations ? data.conversations.length : 0);
      
      return data.conversations || [];
    } catch (error) {
      console.error('🔄 Error fetching conversations:', error);
      console.error('🔄 Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  // Get messages for a specific conversation with pagination
  async getConversationMessages(conversationId, offset = 0, limit = 50) {
    try {
      const response = await this.rateLimitedFetch(
        `${this.baseURL}/conversations/${conversationId}/messages?offset=${offset}&limit=${limit}`, 
        {
          method: 'GET',
          headers: this.getAuthHeaders()
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        messages: data.messages || [],
        pagination: data.pagination || { hasMore: false, total: 0 }
      };
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
      throw error;
    }
  }

  // Create a new conversation
  async createConversation(businessType = 'general', language = 'en', title = null, firstMessage = null) {
    try {
      console.log('Creating conversation:', { businessType, language, title, firstMessage });
      
      const response = await this.rateLimitedFetch(`${this.baseURL}/conversations`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ businessType, language, title, firstMessage })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Conversation created successfully:', data);
      return data.conversation;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }

  // Get conversation by secure ID
  async getConversationBySecureId(secureId) {
    try {
      const response = await this.rateLimitedFetch(`${this.baseURL}/conversations/secure/${secureId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.conversation;
    } catch (error) {
      console.error('Error fetching conversation by secure ID:', error);
      throw error;
    }
  }

  // Update conversation title
  async updateConversationTitle(conversationId, title) {
    try {
      const response = await this.rateLimitedFetch(`${this.baseURL}/conversations/${conversationId}/title`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ title })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.conversation;
    } catch (error) {
      console.error('Error updating conversation title:', error);
      throw error;
    }
  }

  // Start a new chat session with OpenAI title generation
  async startChatSessionWithTitle(userMessage, businessType = 'general', language = 'en') {
    try {
      console.log('Starting chat session with title generation:', { userMessage, businessType, language });
      
      // First, create a conversation without the first message to avoid duplication
      const conversation = await this.createConversation(businessType, language, 'New Chat');
      console.log('Created conversation:', conversation);
      
      if (!conversation || !conversation.id) {
        throw new Error('Failed to create conversation');
      }
      
      // Send the message to OpenAI to get response and generate title
      console.log('Sending message to OpenAI...');
      const response = await this.rateLimitedFetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          message: userMessage,
          businessType,
          language: language || 'en'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('OpenAI response:', data);
      
      // Update the conversation with the AI-generated title
      if (data.title && conversation.id) {
        try {
          console.log('Updating conversation title to:', data.title);
          await this.updateConversationTitle(conversation.id, data.title);
        } catch (titleError) {
          console.warn('Failed to update conversation title:', titleError);
          // Continue without title update
        }
      }
      
      // Add the user message to the conversation first
      if (conversation.id && userMessage) {
        try {
          console.log('Adding user message to conversation');
          await this.addMessage(conversation.id, 'user', userMessage, {
            businessType: businessType || 'general',
            language: language || 'en'
          });
        } catch (userMessageError) {
          console.warn('Failed to add user message to conversation:', userMessageError);
          // Continue without user message save
        }
      }
      
      // Add the AI response to the conversation
      if (conversation.id && data.response) {
        try {
          console.log('Adding AI response to conversation');
          await this.addMessage(conversation.id, 'assistant', data.response, {
            businessType: businessType || 'general',
            language: language || 'en'
          });
        } catch (messageError) {
          console.warn('Failed to add AI response to conversation:', messageError);
          // Continue without message save
        }
      }
      
      const result = {
        conversation,
        aiResponse: data.response || 'Sorry, I encountered an error processing your request.',
        title: data.title || 'New Chat'
      };
      
      console.log('Chat session created successfully:', result);
      return result;
      
    } catch (error) {
      console.error('Error starting chat session with title:', error);
      
      // Return a fallback response
      return {
        conversation: null,
        aiResponse: 'Sorry, I encountered an error. Please try again.',
        title: 'New Chat',
        error: error.message
      };
    }
  }

  // Add a message to a conversation
  async addMessage(conversationId, role, content, metadata = {}) {
    try {
      console.log('Adding message to conversation:', { conversationId, role, content, metadata });
      
      const response = await this.rateLimitedFetch(`${this.baseURL}/messages`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          conversationId,
          role,
          content,
          metadata
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Message added successfully:', data);
      return data.message;
    } catch (error) {
      console.error('Error adding message:', error);
      throw error;
    }
  }

  // Search conversations by content
  async searchConversations(searchTerm, limit = 20) {
    try {
      console.log('🔍 ChatHistoryService.searchConversations called with:', { searchTerm, limit });
      console.log('🔍 Base URL:', this.baseURL);
      console.log('🔍 Auth headers:', this.getAuthHeaders());
      
      const url = `${this.baseURL}/search?q=${encodeURIComponent(searchTerm)}&limit=${limit}`;
      console.log('🔍 Making request to:', url);
      
      const response = await this.rateLimitedFetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response ok:', response.ok);
      console.log('🔍 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorText = await response.clone().text();
          console.error('🔍 HTTP error response:', errorText);
          errorMessage += `, message: ${errorText}`;
        } catch (textError) {
          console.error('🔍 Could not read error text:', textError);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('🔍 Response data:', data);
      console.log('🔍 Returning conversations:', data.conversations || []);
      return data.conversations || [];
    } catch (error) {
      console.error('🔍 Error searching conversations:', error);
      console.error('🔍 Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  // Delete a conversation
  async deleteConversation(conversationId) {
    try {
      const response = await this.rateLimitedFetch(`${this.baseURL}/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  }

  // Get conversation statistics
  async getStats() {
    try {
      const response = await this.rateLimitedFetch(`${this.baseURL}/stats`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.stats;
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw error;
    }
  }

  // Format conversation for display (ChatGPT-style)
  formatConversationForDisplay(conversation) {
    const formatted = {
      sessionId: conversation.conversation_id,
      conversationId: conversation.conversation_id, // Add explicit conversationId field
      id: conversation.conversation_id, // Add id field for compatibility
      conversation_id: conversation.conversation_id, // Add for compatibility
      secureId: conversation.secure_id, // Add secure ID for URLs
      secure_id: conversation.secure_id, // Add for compatibility
      title: conversation.title || 'New Chat',
      businessType: conversation.business_type || 'general',
      business_type: conversation.business_type || 'general', // Add for compatibility
      language: conversation.language || 'en',
      messageCount: parseInt(conversation.message_count) || 0,
      message_count: parseInt(conversation.message_count) || 0, // Add for compatibility
      lastMessage: conversation.last_message || conversation.lastMessage || '',
      last_message: conversation.last_message || conversation.lastMessage || '', // Add for compatibility
      createdAt: conversation.created_at ? new Date(conversation.created_at) : new Date(),
      created_at: conversation.created_at, // Add for compatibility
      updatedAt: conversation.last_message_at ? new Date(conversation.last_message_at) : new Date(),
      updated_at: conversation.last_message_at, // Add for compatibility
      last_message_at: conversation.last_message_at, // Add for compatibility
      // For compatibility with existing ChatHistory component
      messages: []
    };
    console.log('formatConversationForDisplay - input:', conversation);
    console.log('formatConversationForDisplay - output:', formatted);
    return formatted;
  }

  // Convert messages to ChatHistory format
  formatMessagesForDisplay(messages) {
    return messages.map(msg => ({
      type: msg.role === 'user' ? 'user' : 'answer',
      content: msg.content,
      timestamp: new Date(msg.created_at),
      id: msg.id
    }));
  }

  // Public method to cancel all pending requests
  cancelRequests() {
    this.cancelAllRequests();
  }

  // Get current request status
  getRequestStatus() {
    return {
      activeRequests: this.activeRequests,
      maxConcurrentRequests: this.maxConcurrentRequests,
      queueLength: this.requestQueue.length,
      isProcessingQueue: this.isProcessingQueue
    };
  }
}

export default new ChatHistoryService();
